import { useState } from "react";
import useSWR from "swr";
import { toast } from "sonner";
import { Download, KeyRound, Plus, Trash2 } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { downloadCertificate } from "@/lib/certificate";
import { api, errMsg, fetcher } from "@/lib/api";

const ROLES = [
  { id: "admin", label: "Administrador", desc: "Acceso total, integraciones y equipo" },
  { id: "editor", label: "Editor", desc: "Aprueba, publica y difunde" },
  { id: "redactor", label: "Redactor", desc: "Ingesta y redacción de borradores" },
];

export default function Equipo() {
  const { data, mutate, error } = useSWR("/users", fetcher);
  const { data: quizzes } = useSWR("/training/results", fetcher);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "redactor" });
  const [pwd, setPwd] = useState(null);
  const [busy, setBusy] = useState(false);

  if (error) {
    return (
      <div>
        <PageHeader kicker="Equipo" title="Usuarios y roles" />
        <p className="text-sm text-muted-foreground border border-dashed border-border p-8 text-center">
          Solo los administradores pueden gestionar el equipo.
        </p>
      </div>
    );
  }

  const create = async () => {
    try {
      await api.post("/users", form);
      toast.success("Usuario creado");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "redactor" });
      mutate();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const savePassword = async () => {
    setBusy(true);
    try {
      if (pwd.mode === "self") {
        await api.post("/auth/change-password", {
          current_password: pwd.current, new_password: pwd.next,
        });
      } else {
        await api.post(`/users/${pwd.userId}/password`, { new_password: pwd.next });
      }
      toast.success("Contraseña actualizada");
      setPwd(null);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        kicker="Equipo"
        title="Usuarios y roles"
        description="Control de acceso por rol. Las contraseñas se almacenan con bcrypt y los intentos fallidos se bloquean temporalmente."
      >
        <Button variant="outline" onClick={() => setPwd({ mode: "self", current: "", next: "" })}
                data-testid="change-my-password-btn">
          <KeyRound className="h-4 w-4 mr-2" />Cambiar mi contraseña
        </Button>
        <Button onClick={() => setOpen(true)} data-testid="new-user-btn">
          <Plus className="h-4 w-4 mr-2" />Nuevo usuario
        </Button>
      </PageHeader>

      <div className="grid md:grid-cols-3 gap-4 mb-8">
        {ROLES.map((r) => (
          <div key={r.id} className="border border-border bg-card p-5">
            <div className="kicker text-primary">{r.label}</div>
            <p className="text-sm text-muted-foreground mt-1">{r.desc}</p>
          </div>
        ))}
      </div>

      <div className="border border-border bg-card divide-y divide-border" data-testid="users-list">
        {(data || []).map((u) => (
          <div key={u.id} className="p-4 flex items-center justify-between gap-3" data-testid={`user-${u.id}`}>
            <div className="min-w-0">
              <div className="text-sm font-medium">{u.name}</div>
              <div className="text-xs text-muted-foreground truncate">{u.email}</div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  Meta
                </span>
                <Input type="number" min="0" max="200" defaultValue={u.weekly_goal ?? 10}
                  className="h-8 w-16 text-sm"
                  onBlur={async (e) => {
                    const goal = Number(e.target.value);
                    try {
                      await api.post(`/users/${u.id}/goal`, { weekly_goal: goal });
                      toast.success(`Meta semanal de ${u.name || u.email}: ${goal} notas`);
                      mutate();
                    } catch (err) {
                      toast.error(errMsg(err));
                    }
                  }}
                  data-testid={`goal-${u.id}`} />
              </div>
              <Badge variant="secondary">{u.role}</Badge>
              <Button size="sm" variant="outline"
                onClick={() => setPwd({ mode: "admin", userId: u.id, name: u.name, next: "" })}
                data-testid={`reset-password-${u.id}`}>
                <KeyRound className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="outline" onClick={async () => { await api.delete(`/users/${u.id}`).catch((e) => toast.error(errMsg(e))); mutate(); }} data-testid={`delete-user-${u.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <div className="border border-border bg-card p-6 mt-8" data-testid="training-panel">
        <div className="kicker text-primary mb-2">Evaluación interna del recorrido</div>
        <p className="text-sm text-muted-foreground mb-4">
          Resultado del cuestionario que cierra el recorrido guiado. Se aprueba con{" "}
          {quizzes?.pass_score ?? 8} de {quizzes?.total ?? 10} respuestas correctas y se puede
          repetir cuantas veces sea necesario.
        </p>
        <div className="divide-y divide-border" data-testid="training-results">
          {(quizzes?.items || []).map((r) => (
            <div key={r.user_id} className="py-3 flex items-center justify-between gap-3"
                 data-testid={`training-${r.user_id}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium">{r.name || r.email}</div>
                <div className="text-xs text-muted-foreground">
                  {r.attempts === 0 ? "Sin presentar" :
                    `${r.attempts} intento(s) · último ${new Date(r.last_at).toLocaleDateString("es-DO")}`}
                  {r.folio ? ` · folio ${r.folio}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-sm mono">
                  {r.best === null ? "—" : `${r.best}/${quizzes?.total ?? 10}`}
                </span>
                <Badge variant={r.passed ? "default" : "secondary"}>
                  {r.attempts === 0 ? "pendiente" : r.passed ? "aprobado" : "no aprobado"}
                </Badge>
                {r.passed && (
                  <Button size="sm" variant="outline"
                          onClick={() => downloadCertificate(r.user_id, r.name)}
                          data-testid={`certificate-${r.user_id}`}>
                    <Download className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader><DialogTitle className="serif text-2xl">Nuevo usuario</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase tracking-wider">Nombre</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="user-name-input" /></div>
            <div><Label className="text-xs uppercase tracking-wider">Correo</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="user-email-input" /></div>
            <div><Label className="text-xs uppercase tracking-wider">Contraseña</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" data-testid="user-password-input" /></div>
            <div><Label className="text-xs uppercase tracking-wider">Rol</Label>
              <select className="w-full mt-1.5 h-10 border border-input bg-background px-3 text-sm rounded-md"
                value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role-select">
                {ROLES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select></div>
          </div>
          <DialogFooter>
            <Button onClick={create} disabled={!form.email || !form.password} data-testid="save-user-btn">Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwd} onOpenChange={(o) => !o && setPwd(null)}>
        <DialogContent data-testid="password-dialog">
          <DialogHeader>
            <DialogTitle className="serif text-2xl">
              {pwd?.mode === "self" ? "Cambiar mi contraseña" : `Restablecer contraseña de ${pwd?.name || ""}`}
            </DialogTitle>
          </DialogHeader>
          {pwd && (
            <div className="space-y-3">
              {pwd.mode === "self" && (
                <div>
                  <Label className="text-xs uppercase tracking-wider">Contraseña actual</Label>
                  <Input type="password" value={pwd.current}
                    onChange={(e) => setPwd({ ...pwd, current: e.target.value })}
                    className="mt-1.5" data-testid="current-password-input" />
                </div>
              )}
              <div>
                <Label className="text-xs uppercase tracking-wider">Nueva contraseña</Label>
                <Input type="password" value={pwd.next}
                  onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
                  className="mt-1.5" data-testid="new-password-input" />
                <p className="text-xs text-muted-foreground mt-1">
                  Mínimo 10 caracteres, combinando letras, números y símbolos.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwd(null)}>Cancelar</Button>
            <Button onClick={savePassword} disabled={busy || !pwd?.next}
                    data-testid="save-password-btn">
              {busy ? "Guardando…" : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
